import { array } from '@ember/helper'
import { and } from 'ember-truth-helpers'
import { currentStandardsSetUnsupported } from 'teacher-ui-client/helpers/standards-set'
import { exportFilename } from 'teacher-ui-client/services/reporting'
import {
  InlineAlert,
  AlertType,
  InlineAlertSubtitle,
  InlineAlertTitle,
} from 'teacher-ui-client/components/inline-alert'
import { keepLatest } from 'reactiveweb/keep-latest'
import {
  LessonResults,
  type ResultsReportData,
} from 'teacher-ui-client/components/reporting/lesson-performance/results'
import {
  LessonSelectionPanel,
  type LessonSelectionData,
  type LessonStandard,
} from 'teacher-ui-client/components/reporting/lesson-performance/lesson-selection-panel'
import { NoStandardsSetAlert } from 'teacher-ui-client/components/standards-set/no-standards-set-alert'
import {
  PerformanceOverview,
  type LessonsOverviewReportData,
} from 'teacher-ui-client/components/reporting/performance-overview'
import { Product } from 'teacher-ui-client/utils/products'
import { ReportingControlsGap } from 'teacher-ui-client/components/reporting/controls-gap'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import { ReportingPrintButton } from 'teacher-ui-client/components/reporting/print-button'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingUiScopeSelect } from 'teacher-ui-client/components/reporting/ui-scope-select'
import { service } from '@ember/service'
import { StandardsSetUnsupportedAlert } from 'teacher-ui-client/components/standards-set/unsupported-alert'
import { StudentSearchComponent } from 'teacher-ui-client/components/student-search'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { t } from 'ember-intl'
import { task } from 'ember-concurrency'
import { trackedFunction } from 'reactiveweb/function'
import { use } from 'ember-resources'
import Component from '@glimmer/component'
import NoReportValue from 'teacher-ui-client/components/reporting/no-report-value'
import ReportingControls from 'teacher-ui-client/components/reporting/controls'
import RouteTemplate from 'ember-route-template'
import type { RexLessonPerformanceController } from 'teacher-ui-client/reporting/ui-scope/rex/lesson-performance/controller'
import type { RexLessonPerformanceRoute } from 'teacher-ui-client/reporting/ui-scope/rex/lesson-performance/route'
import type { TOC } from '@ember/component/template-only'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import type ReportingService from 'teacher-ui-client/services/reporting'
import type SessionService from 'teacher-ui-client/services/session'

interface Signature {
  Args: {
    model: ModelFor<RexLessonPerformanceRoute>
    controller: RexLessonPerformanceController
  }
}

export class RexLessonPerformance extends Component<Signature> {
  @service reporting!: ReportingService
  @service lessonInfo!: LessonInfoService
  @service session!: SessionService

  fetchLessonSelection = trackedFunction(this, () => {
    return this.lessonInfo.fetchLessonStandards<LessonSelectionData>({
      product: Product.REX,
      standardsSetId: this.session.currentSchool.standardsSet?.id ?? null,
    })
  })

  fetchOverviewReport = trackedFunction(this, () => {
    const url = this.reporting.buildGravityUrl({
      product: Product.REX,
      requestScope: this.args.model.requestScope,
      reportName: 'lesson-performance/overview',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      filter: { lesson: this.args.controller.selectedLesson },
    })

    return this.reporting.fetchReport<LessonsOverviewReportData>(url, query)
  })

  fetchResultsReport = trackedFunction(this, () => {
    const url = this.reporting.buildGravityUrl({
      product: Product.REX,
      requestScope: this.args.model.requestScope,
      reportName: 'lesson-performance/results',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      filter: { lesson: this.args.controller.selectedLesson },
      page: { number: this.args.controller.pageNumber, size: this.args.controller.perPage },
      sort: this.args.controller.sort,
    })

    return this.reporting.fetchReport<ResultsReportData>(url, query)
  })

  @use
  latestLessonData = keepLatest({
    value: () => this.fetchLessonSelection.value,
    when: () => this.fetchLessonSelection.isPending,
  })

  @use
  latestOverviewData = keepLatest({
    value: () => this.fetchOverviewReport.value,
    when: () => this.fetchOverviewReport.isPending,
  })

  @use
  latestResultsData = keepLatest({
    value: () => this.fetchResultsReport.value,
    when: () => this.fetchResultsReport.isPending,
  })

  get lessonData() {
    return this.latestLessonData?.lesson_standards.length === 0 ? undefined : this.latestLessonData
  }

  get overviewData() {
    return this.latestOverviewData?.overview.total_students ? this.latestOverviewData : undefined
  }

  exportResultsCsv = task({ drop: true }, async () => {
    const url = this.reporting.buildGravityUrl({
      product: Product.REX,
      requestScope: this.args.model.requestScope,
      reportName: 'lesson-performance/results',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      filter: { lesson: this.args.controller.selectedLesson },
      sort: this.args.controller.sort,
    })
    const filename = exportFilename(Product.REX, 'lesson-performance', '.csv')

    await this.reporting.exportReportCsv(url, query, filename)
  })

  updateSelectedLesson = (lesson: number) => {
    this.args.controller.pageNumber = 1
    this.args.controller.selectedLesson = lesson
  }

  get selectedLessonData(): LessonStandard | undefined {
    return this.latestLessonData?.lesson_standards.find(
      (lessonItem) => lessonItem.lesson === this.args.controller.selectedLesson,
    )
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Reading)
  }

  get isMissingStandards() {
    return !this.session.currentSchool.standardsSet
  }

  get isOneRosterEnabled() {
    return Boolean(this.session.currentSchool.oneRosterEnabledAt)
  }

  <template>
    <ReportCoverageAlert @class="mt-3 mb-5" />
    <ReportingTeacherUiHeader @title={{t "reporting.pageTitles.lessonPerformance"}} />
    <ReportingControls>
      <ReportingUiScopeSelect class="shrink basis-80" @disabled={{this.isMissingStandards}} />
      <ReportingControlsGap />
      <ReportingPeriodSelect @disabled={{this.isMissingStandards}} />
      <ReportingPrintButton @disabled={{this.isMissingStandards}} />
      <ReportingControlsGap />
      <StudentSearchComponent
        @studentRouteName="reporting.ui-scope.rex.student-summary"
        @students={{@model.scopedStudents}}
        @includeSelectAll={{false}}
      />
    </ReportingControls>
    <div class="space-y-6">
      {{#if (currentStandardsSetUnsupported)}}
        <StandardsSetUnsupportedAlert />
      {{else}}
        {{#if this.isMissingStandards}}
          <NoStandardsSetAlert
            @hasDisco={{Boolean this.session.currentSchool.district.districtCoordinators.length}}
            @isSubco={{this.isSubco}}
            @isOneRosterEnabled={{this.isOneRosterEnabled}}
          />
        {{else}}
          {{#if this.lessonData}}
            <LessonSelectionPanel
              @lessonData={{this.lessonData.lesson_standards}}
              @selectedLesson={{@controller.selectedLesson}}
              @setLesson={{this.updateSelectedLesson}}
              @standardsSet={{this.session.currentSchool.standardsSet.name}}
            />
          {{/if}}
          {{#if (and this.lessonData this.overviewData this.latestResultsData)}}
            {{#if this.overviewData}}
              <PerformanceOverview
                @reportData={{this.overviewData}}
                @isSubco={{this.isSubco}}
                @isReloading={{this.fetchOverviewReport.isPending}}
              />
            {{/if}}
            {{#if this.latestResultsData}}
              <LessonResults
                @reportData={{this.latestResultsData}}
                @selectedLessonData={{this.selectedLessonData}}
                @exportCsv={{this.exportResultsCsv}}
                @isSubco={{this.isSubco}}
                @isReloading={{this.fetchResultsReport.isPending}}
                @product={{Product.REX}}
              />
            {{/if}}
          {{else}}
            <NoReportValue @reportStates={{array this.fetchOverviewReport this.fetchResultsReport}} />
          {{/if}}
        {{/if}}
      {{/if}}
    </div>
  </template>
}

interface AlertSignature {
  Element: HTMLDivElement
  Args: {
    class: string
  }
}

export const ReportCoverageAlert: TOC<AlertSignature> = <template>
  <div class={{@class}}>
    <InlineAlert data-test-report-coverage-alert @type={{AlertType.Critical}}>
      <InlineAlertTitle>
        {{t "reporting.lessonPerformance.reportCoverageAlert.title"}}
      </InlineAlertTitle>
      <InlineAlertSubtitle class="max-w-[750px]">
        {{t "reporting.lessonPerformance.reportCoverageAlert.subTitle" htmlSafe=true}}
      </InlineAlertSubtitle>
    </InlineAlert>
  </div>
</template>

export default RouteTemplate(RexLessonPerformance)
